/**
 * GCS storage adapter for editor media files.
 *
 * Uploads blobs to GCS via signed URLs (through the backend API),
 * and downloads them back via signed read URLs.
 *
 * This replaces OPFSAdapter for persistent, cross-device media storage.
 */

import { apiFetch } from "@/auth/api";

interface UploadUrlResponse {
	signedUrl: string;
	gcsPath: string;
	contentType: string;
	maxBytes: number;
}

interface PlayUrlResponse {
	playUrl: string;
}

export class GCSAdapter {
	private projectId: string;

	constructor(projectId: string) {
		this.projectId = projectId;
	}

	/**
	 * Upload a file to GCS via signed URL.
	 * Returns the gcsPath for later retrieval.
	 */
	async upload({
		mediaId,
		file,
	}: {
		mediaId: string;
		file: File;
	}): Promise<string> {
		// 1. Get signed upload URL from backend
		const resp = await apiFetch("/api/videos/editor-media-upload-url", {
			method: "POST",
			body: JSON.stringify({
				projectId: this.projectId,
				mediaId,
				filename: file.name,
				contentType: file.type || "application/octet-stream",
			}),
		});

		if (!resp.ok) {
			const body = await resp.json().catch(() => ({}));
			throw new Error(
				(body as { error?: string }).error || `Upload URL failed: HTTP ${resp.status}`,
			);
		}

		const { signedUrl, gcsPath, contentType } =
			(await resp.json()) as UploadUrlResponse;

		// 2. PUT the file directly to GCS
		const putResp = await fetch(signedUrl, {
			method: "PUT",
			headers: { "Content-Type": contentType },
			body: file,
		});

		if (!putResp.ok) {
			throw new Error(`GCS upload failed: HTTP ${putResp.status}`);
		}

		return gcsPath;
	}

	/**
	 * Download a file from GCS via signed read URL.
	 * Returns a File object.
	 */
	async download({
		mediaId,
		gcsPath,
		filename,
	}: {
		mediaId: string;
		gcsPath: string;
		filename: string;
	}): Promise<File> {
		// 1. Get signed read URL from backend
		const resp = await apiFetch(
			`/api/videos/play-url?gcsPath=${encodeURIComponent(gcsPath)}`,
		);

		if (!resp.ok) {
			throw new Error(`Play URL failed for ${mediaId}: HTTP ${resp.status}`);
		}

		const { playUrl } = (await resp.json()) as PlayUrlResponse;

		// 2. Fetch the actual file blob
		const fileResp = await fetch(playUrl);
		if (!fileResp.ok) {
			throw new Error(
				`GCS download failed for ${mediaId}: HTTP ${fileResp.status}`,
			);
		}

		const blob = await fileResp.blob();
		return new File([blob], filename, { type: blob.type });
	}
}
